<script setup>
import { computed, ref, nextTick, onMounted } from 'vue';
import BaseListCard from './BaseListCard.vue';
import { makeFilterButtonHTML } from '../utils/make-filter-button-html';
import { makeMapButtonHTML } from '../utils/make-map-button-html';
import BaseFilters from './BaseFilters.vue';
import BaseSearchInput from './BaseSearchInput.vue';

const props = defineProps({
  datasets: {
    type: Array,
    required: true,
  },
  selectedDatasets: {
    type: Array,
    required: true,
  },
  primaryColor: {
    type: String,
    required: true,
  },
});

const searchQuery = ref('');

const filteredLocations = computed(() => {
  let locations = props.datasets
    .filter(dataset => props.selectedDatasets.includes(dataset.id))
    .flatMap(dataset => dataset.features?.map(feature => {
      const tooltipData = feature.properties?.tooltip || [];
      const coords = feature.geometry.coordinates;
      
      // Collect all searchable text
      const searchableText = [
        tooltipData.find(t => t.layout === 'meta')?.meta,
        tooltipData.find(t => t.layout === 'title')?.title,
        tooltipData.find(t => t.layout === 'text')?.text,
        feature.properties?.name,
        feature.properties?.description,
        coords ? `${coords[1]},${coords[0]}` : null
      ]
        .filter(Boolean)
        .join(' ');

      return {
        ...feature,
        datasetId: dataset.id,
        datasetTitle: dataset.title,
        tooltipArray: tooltipData,
        searchableText: searchableText.toLowerCase()
      };
    }));

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    locations = locations.filter(location => 
      location.searchableText.includes(query)
    );
  }

  return locations;
});

const emits = defineEmits(['toggleView', 'datasetChange']);

const showFilters = ref(false);

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const toggleView = () => {
  emits('toggleView');
};

const filterButtonHTML = computed(() => makeFilterButtonHTML('Filter', props.primaryColor));
const mapButtonHTML = computed(() => makeMapButtonHTML('Kaart', props.primaryColor));

const handleDatasetChange = (id, checked) => {
  emits('datasetChange', id, checked);
};

const closeFilters = () => {
  showFilters.value = false;
};

const ITEMS_PER_PAGE = 12;
const displayLimit = ref(ITEMS_PER_PAGE);
const listItemRefs = ref([]);

const paginatedLocations = computed(() => {
  return filteredLocations.value.slice(0, displayLimit.value);
});

const hasMoreItems = computed(() => {
  return displayLimit.value < filteredLocations.value.length;
});

const loadMore = () => {
  const newLimit = displayLimit.value + ITEMS_PER_PAGE;
  displayLimit.value = Math.min(newLimit, filteredLocations.value.length);

  nextTick(() => {
    const newItemIndex = displayLimit.value - ITEMS_PER_PAGE;
    const newItemRef = listItemRefs.value[newItemIndex];
    if (newItemRef) {
      newItemRef.focus();
    }
  });
};

const handleSearch = (query) => {
  searchQuery.value = query;
};

onMounted(() => {
  if (window.innerWidth > 768) {
    showFilters.value = true;
  }
});

</script>

<template>
  <div
    class="list-view"
    :class="{
      'filters-open': showFilters,
      }"
  >
    <div class="list-view__controls">
      <BaseSearchInput 
        :primary-color="primaryColor"
        :results-count="filteredLocations.length"
        @search="handleSearch"
      />
      <button @click="toggleView" class="list-view__map-button" v-html="mapButtonHTML"></button>
      <button
        v-if="datasets.length > 0"
        @click="toggleFilters"
        class="list-view__filters-button"
        v-html="filterButtonHTML"
      ></button>
    </div>

    <!-- Add fade transition for overlay -->
    <Transition name="fade">
      <div
        v-if="showFilters"
        class="owc-openkaarten-streetmap__overlay"
      ></div>
    </Transition>

    <!-- Add slide transition for filters -->
    <Transition name="slide">
      <BaseFilters
        v-if="showFilters"
        :open="showFilters"
        :datasets="datasets.filter((set) => set.features?.length && !set.features?.every(feature => feature.geometry?.type === 'Polygon'))"
        :selectedDatasets="selectedDatasets"
        :primaryColor="primaryColor"
        @closeFilters="closeFilters"
        @datasetChange="handleDatasetChange"
      />
    </Transition>

    <ul class="list-view__results">
      <li
        v-for="(location, index) in paginatedLocations"
        :key="`${location?.datasetId}-${location?.properties.id}`"
        class="list-view__item"
        :ref="el => { listItemRefs[index] = el }"
        tabindex="0"
      >
        <BaseListCard
          v-if="location"
          :tooltipArray="location.tooltipArray"
          :datasetTitle="location.datasetTitle"
          :primaryColor="primaryColor"
          :marker="location.properties.marker"
        />
      </li>

      <!-- Add Load More button -->
      <button
        v-if="hasMoreItems"
        @click="loadMore"
        class="list-view__load-more"
      >
        Toon meer resultaten ({{ paginatedLocations.length }} van {{ filteredLocations.length }})
      </button>
    </ul>
  </div>
</template>

<style lang="scss">
.list-view {
	background-color: #E5E5E6;
	display: flex;
	flex-direction: column;
	inline-size: 100%;
	min-block-size: 660px;
	overflow-x: clip;
	padding: 1rem;
	padding-block-start: 0;
	position: absolute;
	z-index: 9999;

	&__controls {
		align-items: center;
		block-size: 76px;
		border-block-end: 1px solid var(--Neutral-300, #e5e5e6);
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		margin-block-end: 2.5rem;
		transition: all 0.2s ease-in-out;

		@media only screen and (min-width: 768px) {
			justify-content: flex-end;
			margin-block-end: 0;

			.filters-open & {
				margin-inline-end: 155px;

				.list-view__filters-button {
					opacity: 0;
					pointer-events: none;
				}
			}
		}

		button:not([class*="search"]) {
			align-items: center;
			background-color: #fff;
			border: 1px solid var(--owc-openkaarten-streetmap--primary-color);
			border-radius: 3px;
			display: flex;
			gap: 8px;
			justify-content: space-between;
			min-block-size: 48px;
			min-inline-size: 44px;
			padding: 10px 17px;

			span {
				color: var(--owc-openkaarten-streetmap--primary-color);
				font-size: 20px;
				font-style: normal;
				font-weight: 500;
				line-height: 130%;
			}

			&:hover {
				background-color: rgb(244, 244, 244);
				cursor: pointer;
			}

			&:focus-visible {
				border-width: 3px;
			}
		}
	}

	&__results {
		display: flex;
		flex-direction: column;
		margin: 0;
		max-block-size: 555px;
		overflow: scroll;
		padding: 0;

		.filters-open & {
			max-inline-size: calc(100% - 267px);
		}
	}

	&__item {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		&:focus {
			outline: 2px solid var(--owc-openkaarten-streetmap--primary-color);
			outline-offset: 1px;
		}
	}

	&__load-more {
		background-color: white;
		border: 2px solid var(--button-color);
		border-radius: 4px;
		color: var(--button-color);
		cursor: pointer;
		font-size: 1rem;
		font-weight: 500;
		margin-block-start: 1rem;
		margin-inline: auto;
		padding: 1rem 2rem;
		transition: background-color 0.2s ease;

		&:hover {
			background-color: rgb(244, 244, 244);
		}

		&:focus-visible {
			outline: 2px solid var(--button-color);
			outline-offset: 2px;
		}
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
	transform: translateX(0);
	transition: transform 0.2s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
	transform: translateX(120%);
}

.owc-openkaarten-streetmap__overlay {
	background-color: var(--owc-map-overlay, rgba(0, 0, 0, 0.25));
	block-size: 100%;
	content: '';
	inline-size: 100%;
	inset-block-start: 0;
	inset-inline-start: 0;
	position: absolute;
	z-index: 999;
}
</style>
